import { parseJsonOrNull } from "replo-utils/lib/json";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { parseSchemaOrNull } from "replo-utils/lib/parse";
import { z } from "zod";

export const SELECT_MODE_SELECTABLE_ATTRIBUTE = "data-replo-selectable";
export const SELECT_MODE_SELECTABLE_PAYLOAD_ATTRIBUTE =
  "data-replo-selectable-payload";

export const displayModeSchema = z
  .enum(["browse", "selection"])
  .describe("DisplayMode");

// "translated" marks copy rendered from a `dict.*` dictionary lookup (the
// site-i18n convention): inline-editable, and the commit writes the
// dictionaries rather than the JSX.
export const selectModeInlineTextStatusSchema = z
  .enum(["editable", "dynamic", "translated"])
  .nullable()
  .describe("SelectModeInlineTextStatus");

export const selectModeTextMetricsSchema = z
  .object({
    tightWidth: z.number(),
    lineCount: z.number(),
    lineHeight: z.number(),
  })
  .describe("SelectModeTextMetrics");

export const selectModeClickPositionSchema = z
  .object({
    x: z.number(),
    y: z.number(),
  })
  .describe("SelectModeClickPosition");

export const selectModeSourceLocationSchema = z
  .object({
    filePath: z.string(),
    lineNumber: z.number(),
    columnNumber: z.number().optional(),
    componentName: z.string().optional(),
  })
  .describe("SelectModeSourceLocation");

export const selectModeFiberChainEntrySchema = z
  .object({
    sourceLocation: selectModeSourceLocationSchema,
    tagName: z.string().optional(),
    componentName: z.string().optional(),
    props: z.record(z.string(), z.unknown()).optional(),
  })
  .describe("SelectModeFiberChainEntry");

export const selectModeBoundingRectSchema = z
  .object({
    x: z.number(),
    y: z.number(),
    left: z.number(),
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    width: z.number(),
    height: z.number(),
  })
  .describe("SelectModeBoundingRect");

// A drag-intersected selectable's box plus a stable id. The id is minted
// once per source element when a drag begins (not per pointer-move), so the
// parent overlay can use it as a stable React key — identical bounds across
// sibling elements would otherwise collide on a bounds-derived key.
export const selectModeSelectionRectSchema = z
  .object({
    id: z.string(),
    rect: selectModeBoundingRectSchema,
  })
  .describe("SelectModeSelectionRect");

export const selectModeStyleAtRuleSchema = z
  .object({
    type: z.string(),
    text: z.string(),
  })
  .describe("SelectModeStyleAtRule");

export const selectModeStyleVariantSchema = z
  .object({
    atRules: z.array(selectModeStyleAtRuleSchema),
    pseudo: z.string().nullable(),
  })
  .describe("SelectModeStyleVariant");

export const selectModeDefinedStyleSchema = z
  .object({
    property: z.string(),
    variant: selectModeStyleVariantSchema.nullable(),
    className: z.string().nullable(),
    raw: z.string(),
    resolved: z.string(),
  })
  .describe("SelectModeDefinedStyle");

export type RectOffset = {
  left: number;
  top: number;
};

export function transformRect({
  rect,
  offset,
  scale = 1,
}: {
  rect: SelectModeBoundingRect;
  offset: RectOffset;
  scale?: number;
}): SelectModeBoundingRect {
  return {
    x: rect.x * scale + offset.left,
    y: rect.y * scale + offset.top,
    left: rect.left * scale + offset.left,
    right: rect.right * scale + offset.left,
    top: rect.top * scale + offset.top,
    bottom: rect.bottom * scale + offset.top,
    width: rect.width * scale,
    height: rect.height * scale,
  };
}

export function transformPoint({
  point,
  offset,
  scale = 1,
}: {
  point: SelectModeClickPosition;
  offset: RectOffset;
  scale?: number;
}): SelectModeClickPosition {
  return {
    x: point.x * scale + offset.left,
    y: point.y * scale + offset.top,
  };
}

export function getIframeOffset(iframe: HTMLIFrameElement | null): RectOffset {
  if (!iframe) {
    return { left: 0, top: 0 };
  }
  const iframeRect = iframe.getBoundingClientRect();
  return { left: iframeRect.left, top: iframeRect.top };
}

export const selectedElementSchema = z
  .object({
    kind: z.literal("react-element"),
    selector: z.string(),
    tagName: z.string(),
    elementType: z.string(),
    textContent: z.string().nullable(),
    rect: selectModeBoundingRectSchema,
    textMetrics: selectModeTextMetricsSchema.nullable(),
    outerHTML: z.string(),
    definedStyles: z.array(selectModeDefinedStyleSchema).default([]),
    inheritedStyles: z.record(z.string(), z.string()).default({}),
    clickPosition: selectModeClickPositionSchema,
    sourceLocation: selectModeSourceLocationSchema.optional(),
    fiberChain: z.array(selectModeFiberChainEntrySchema).default([]),
    siblingSelectors: z.array(z.string()).default([]),
  })
  .describe("SelectedElement");

export const selectablePlanDataSchema = z
  .object({
    kind: z.literal("plan"),
    planId: z.string(),
    label: z.string(),
    rect: selectModeBoundingRectSchema,
  })
  .describe("SelectablePlanData");

export const selectablePlanPayloadSchema = selectablePlanDataSchema
  .omit({ rect: true })
  .describe("SelectablePlanPayload");

export const selectableDataPayloadSchema = z
  .discriminatedUnion("kind", [selectablePlanPayloadSchema])
  .describe("SelectableDataPayload");

export const selectableDataSchema = z
  .discriminatedUnion("kind", [selectedElementSchema, selectablePlanDataSchema])
  .describe("SelectableData");

export type SelectModeBoundingRect = z.infer<
  typeof selectModeBoundingRectSchema
>;
export type SelectModeClickPosition = z.infer<
  typeof selectModeClickPositionSchema
>;
export type SelectableData = z.infer<typeof selectableDataSchema>;
export type SelectableDataPayload = z.infer<typeof selectableDataPayloadSchema>;

export function transformSelectedElement({
  element,
  offset,
  scale = 1,
}: {
  element: z.infer<typeof selectedElementSchema>;
  offset: RectOffset;
  scale?: number;
}): z.infer<typeof selectedElementSchema> {
  return {
    ...element,
    rect: transformRect({ rect: element.rect, offset, scale }),
    clickPosition: transformPoint({
      point: element.clickPosition,
      offset,
      scale,
    }),
  };
}

export function transformSelectableData({
  data,
  offset,
}: {
  data: SelectableData;
  offset: RectOffset;
}): SelectableData {
  return exhaustiveSwitch(
    data,
    "kind",
  )<SelectableData>({
    "react-element": (element) => transformSelectedElement({ element, offset }),
    plan: (plan) => ({
      ...plan,
      rect: transformRect({ rect: plan.rect, offset }),
    }),
  });
}

export function readSelectablePayload(rawPayload: string | null) {
  if (!rawPayload) {
    return null;
  }

  const parsedPayload = parseJsonOrNull(rawPayload);
  if (!parsedPayload) {
    return null;
  }

  return parseSchemaOrNull(parsedPayload, selectableDataPayloadSchema);
}

export const selectModeOptimisticUpdatePayloadSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("css"),
      property: z.string(),
      value: z.string().nullable(),
    }),
    z.object({
      kind: z.literal("content"),
      innerHTML: z.string(),
    }),
    z.object({
      kind: z.literal("attribute"),
      name: z.string(),
      value: z.string(),
    }),
  ])
  .describe("SelectModeOptimisticUpdatePayload");

export const selectModeTiptapActiveMarksSchema = z
  .object({
    bold: z.boolean(),
    italic: z.boolean(),
    underline: z.boolean(),
    strike: z.boolean(),
  })
  .describe("SelectModeTiptapActiveMarks");

export const selectModeFormatCommandSchema = z
  .enum(["toggleBold", "toggleItalic", "toggleUnderline", "toggleStrike"])
  .describe("SelectModeFormatCommand");

/**
 * Shared discriminator values for select-mode selection state. Single
 * source of truth for the three consumers that must carry every variant
 * in this enum:
 *
 *   - parent agent-ui store: `SelectModeSelection` (defined below)
 *   - persisted side-session metadata:
 *     `SelectModeEditSelectionMetadata` (packages/schemas/agentSession.ts)
 *   - in-iframe SDK: `SelectModeInteractionSession`
 *     (packages/canopy-sdk/editor/select-mode/selectModeSession.ts) — kept
 *     locally there because it holds live DOM `Element` refs and live
 *     drag geometry that aren't expressible in Zod, but its variants
 *     are pinned to this enum via `Extract<SelectModeSelectionType, …>`
 *
 * Renaming or adding a variant here is a TS error in all three.
 *
 * @author Kevin O'Connell 2026-04-20
 */
export const selectModeSelectionTypeSchema = z
  .enum(["none", "element", "area", "drag"])
  .describe("SelectModeSelectionType");

/**
 * Parent-side selection state mirrored from messages the iframe posts
 * back. Lives in agent-ui's `SelectModeStore` and is projected into
 * select-mode side-session metadata at submit time.
 *
 *   - `none`    — nothing selected, the default.
 *   - `element` — a single element was clicked; we have its descriptor.
 *   - `area`    — the user finished a drag; we have the area rect plus
 *                 every element fully contained within it.
 *   - `drag`    — a drag is in progress; we only have the live rect, not
 *                 the eventual element list. The input panel intentionally
 *                 stays hidden while the user is still dragging.
 *
 * Distinct from the SDK-internal `SelectModeInteractionSession`, which
 * holds live DOM `Element` refs and live pointer/scroll geometry. This
 * type holds only the message-passable, serialized projection.
 *
 * @author Kevin O'Connell 2026-04-23
 */
export const selectModeSelectionSchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal("none") }),
    z.object({
      type: z.literal("element"),
      data: selectableDataSchema,
    }),
    z.object({
      type: z.literal("area"),
      rect: selectModeBoundingRectSchema,
      items: z.array(selectableDataSchema),
    }),
    z.object({
      type: z.literal("drag"),
      dragRect: selectModeBoundingRectSchema,
      // Bounding boxes of the selectables the drag currently intersects,
      // rendered as lighter highlights so the user can see what the drag
      // will capture before they release.
      selectionRects: z.array(selectModeSelectionRectSchema),
    }),
  ])
  .describe("SelectModeSelection");
